import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function InstallButton({ className = '' }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const installedHandler = () => {
      setInstalled(true);
      setDeferred(null);
      toast.success('kahrency installed.');
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  if (installed) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') toast.success('Installing…');
      setDeferred(null);
      return;
    }
    if (isIOS()) {
      setIosOpen(true);
      return;
    }
    // Fallback: instructions for desktop
    setIosOpen(true);
  };

  return (
    <>
      <Button
        data-testid="install-app-btn"
        onClick={onClick}
        size="sm"
        className={`gold-btn ${className}`}
      >
        <DownloadIcon /> <span className="ml-2">Install app</span>
      </Button>

      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent data-testid="install-instructions-dialog" className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Install kahrency</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 text-sm text-slate-300">
            <div>
              <div className="label-eyebrow mb-2">iPhone / iPad · Safari</div>
              <ol className="list-decimal list-inside space-y-1.5 text-slate-200">
                <li>Tap the <span className="text-[#D4AF37] font-medium">Share</span> button (the square with the up arrow).</li>
                <li>Scroll and tap <span className="text-[#D4AF37] font-medium">Add to Home Screen</span>.</li>
                <li>Tap <span className="text-[#D4AF37] font-medium">Add</span> — kahrency now lives on your home screen.</li>
              </ol>
            </div>
            <div>
              <div className="label-eyebrow mb-2">Android · Chrome</div>
              <ol className="list-decimal list-inside space-y-1.5 text-slate-200">
                <li>Tap the <span className="text-[#D4AF37] font-medium">⋮ menu</span> (top-right).</li>
                <li>Tap <span className="text-[#D4AF37] font-medium">Install app</span> or <span className="text-[#D4AF37] font-medium">Add to Home screen</span>.</li>
              </ol>
            </div>
            <div>
              <div className="label-eyebrow mb-2">Laptop · Chrome / Edge</div>
              <ol className="list-decimal list-inside space-y-1.5 text-slate-200">
                <li>Click the <span className="text-[#D4AF37] font-medium">install icon</span> in the address bar (looks like a small monitor with a down arrow).</li>
                <li>Or open the browser menu → <span className="text-[#D4AF37] font-medium">Install kahrency…</span></li>
                <li>Click <span className="text-[#D4AF37] font-medium">Install</span>.</li>
              </ol>
            </div>
            <p className="text-xs text-slate-500 pt-2 border-t border-white/5">
              Once installed, kahrency runs in its own window and works offline for browsing past entries.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
